import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Alert } from "../components/ui/Alert";
import { BANNER_BOTTOM_PADDING } from "../components/ui/BugReporterBanner";
import { useDevLogin } from "../hooks/api/useAuth";
import { useAuthStore } from "../stores/authStore";

// 開発用ユーザーリスト（シードデータと同じ）
const DEV_USERS = [
  { email: "admin@mediowl.co.jp", name: "管理者 太郎", role: "admin" },
  { email: "user1@mediowl.co.jp", name: "ユーザー 一郎", role: "user" },
  { email: "user2@mediowl.co.jp", name: "ユーザー 二郎", role: "user" },
  { email: "guest@example.com", name: "ゲスト 三郎", role: "user" },
] as const;

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, setLoading } = useAuthStore();
  const [showDevLogin, setShowDevLogin] = useState(false);

  const devLogin = useDevLogin();

  const error = searchParams.get("error");

  // MSWが無効化されている場合（実API通信モード）のみ開発用ログインを表示
  const isRealApiMode = import.meta.env.VITE_ENABLE_MOCKING === "false";

  useEffect(() => {
    setLoading(false);
  }, [setLoading]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/seats", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleGoogleLogin = () => {
    window.location.href = `${import.meta.env.VITE_API_URL || ""}/api/auth/google`;
  };

  const handleDevLogin = (email: string, name: string) => {
    devLogin.mutate(
      { email, name },
      {
        onSuccess: () => {
          navigate("/seats", { replace: true });
        },
      }
    );
  };

  return (
    <div
      className={`flex min-h-screen items-center justify-center bg-gray-100 ${BANNER_BOTTOM_PADDING}`}
    >
      <div className="w-full max-w-md rounded-lg bg-white p-8 shadow-lg">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-gray-900">座席管理システム</h1>
          <p className="mt-2 text-gray-600">ログインしてください</p>
        </div>

        {error === "unregistered" && (
          <Alert variant="error" className="mb-6">
            このアカウントは登録されていません。招待リンクが必要です。
          </Alert>
        )}

        {error === "invalid_token" && (
          <Alert variant="error" className="mb-6">
            認証に失敗しました。もう一度お試しください。
          </Alert>
        )}

        {devLogin.error && (
          <Alert variant="error" className="mb-6">
            ログインに失敗しました。APIサーバーが起動しているか確認してください。
          </Alert>
        )}

        <button
          type="button"
          onClick={handleGoogleLogin}
          className="flex w-full items-center justify-center gap-3 rounded-md border border-gray-300 bg-white px-4 py-3 text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
        >
          {/* Googleブランドカラー: Google Brand Guidelines準拠のため固定値 */}
          <svg className="h-5 w-5" viewBox="0 0 24 24" aria-label="Google">
            <title>Google</title>
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          <span>Google でログイン</span>
        </button>

        {/* 開発用ログイン（実APIモードのみ表示） */}
        {isRealApiMode && (
          <div className="mt-6 border-t pt-6">
            <button
              type="button"
              onClick={() => setShowDevLogin(!showDevLogin)}
              className="w-full text-center text-sm text-gray-500 hover:text-gray-700"
              aria-expanded={showDevLogin}
              aria-controls="dev-login-section"
            >
              {showDevLogin
                ? "▲ 開発用ログインを閉じる"
                : "▼ 開発用ログイン（Docker環境）"}
            </button>

            {showDevLogin && (
              <div id="dev-login-section" className="mt-4 space-y-2">
                <p className="text-xs text-gray-500 text-center mb-3">
                  Google OAuth なしでログインできます
                </p>
                {DEV_USERS.map((user) => (
                  <button
                    key={user.email}
                    type="button"
                    onClick={() => handleDevLogin(user.email, user.name)}
                    disabled={devLogin.isPending}
                    className="flex w-full items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-100 disabled:opacity-50"
                  >
                    <span>{user.name}</span>
                    <span className="text-xs text-gray-600">{user.role}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="mt-6 text-center">
          <Link
            to="/privacy"
            className="text-xs text-gray-400 hover:text-gray-600"
          >
            プライバシーポリシー
          </Link>
        </div>
      </div>
    </div>
  );
}
